import { getAll } from '@/services/api';
import { getList } from '@/services/global';
import { isStaffRole } from '@/pages/Launchpad/utils/permissions';

import {
  createRPAAgent,
  deleteRPAAgent,
  fetchRpaAgentSessions,
  getRPAAgentById,
  getRpaAgentDashboardSummary,
  patchRPAAgentName,
  revokeRpaAgentSession,
  toggleRPAAgentActive,
} from '../service';
import { isCurrentSession, normalizeSession, normalizeSessions } from './sessionUtils';

const LAUNCHPAD_FIRST_PAGE = 60;
const SESSIONS_PAGE = 50;

const AGENT_SELECT = 'id,name,isActive,agentPlatform,created,updated';

const unwrapEntity = (res) => {
  if (!res) return null;
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  if (typeof res === 'object') return res;
  return null;
};

function* refreshRpaLaunchpad(call, put, select) {
  const roles = yield select((s) => s.user?.currentUser?.roles);
  const includeInactive = isStaffRole(roles);
  try {
    const listRes = yield call(getList, {
      route: 'rpa/api/agent/paged',
      params: {
        page: 1,
        limit: LAUNCHPAD_FIRST_PAGE,
        select: AGENT_SELECT,
        orderBy: 'name',
        ...(!includeInactive ? { filter: 'isActive eq true' } : {}),
      },
      readPriority: true,
    });
    const items = Array.isArray(listRes?.items)
      ? listRes.items
      : Array.isArray(listRes)
      ? listRes
      : [];
    const total =
      typeof listRes?.totalCount === 'number'
        ? listRes.totalCount
        : typeof listRes?.count === 'number'
        ? listRes.count
        : items.length;

    yield put({ type: 'launchpad/homeStart', payload: { type: 'rpa' } });
    yield put({
      type: 'launchpad/homeSave',
      payload: {
        type: 'rpa',
        items,
        groups: [],
        favorites: [],
        progress: { itemsLoaded: items.length, itemsTotal: total },
      },
    });
  } catch {
    yield put({
      type: 'launchpad/fetchHome',
      payload: { type: 'rpa', includeInactive, readPriority: true },
    });
  }
}

export default {
  namespace: 'rpaAgents',
  state: {
    showForm: false,
    showSessions: false,
    id: null,
    item: null,
    detail: true,
    currentSession: null,
    sessions: [],
    sessionsSkip: 0,
    sessionsHasMore: false,
    sessionsLoading: false,
    flows: [],
    flowsLoading: false,
    dashboardSummary: null,
    success: false,
    validationErrors: [],
    loading: false,
    revokeLoading: false,
  },

  effects: {
    *showForm({ payload }, { put }) {
      const { state, id = null, item = null, detail = true } = payload || {};
      yield put({
        type: 'queryShowForm',
        payload: { state: !!state, id, item, detail: !!detail },
      });
      if (state && id) {
        yield put({ type: 'fetchDetail', payload: { id } });
      }
    },

    *showSessions({ payload }, { put }) {
      const { state, id = null, name = null } = payload || {};
      yield put({
        type: 'queryShowSessions',
        payload: { state: !!state, id, name },
      });
      if (state && id) {
        yield put({ type: 'fetchSessions', payload: { id, reset: true } });
      }
    },

    *fetchDetail({ payload }, { call, put }) {
      const agentId = payload?.id;
      if (!agentId) return;
      yield put({ type: 'setLoading', payload: true });
      try {
        const res = yield call(getRPAAgentById, agentId, {
          select: AGENT_SELECT,
        });
        const agent = unwrapEntity(res) || { id: agentId };
        yield put({ type: 'saveItem', payload: agent });

        // Latest session (take=1) for device/presence; 404/empty → unlinked.
        try {
          const sessionsRes = yield call(fetchRpaAgentSessions, agentId, {
            skip: 0,
            take: 1,
          });
          const list = normalizeSessions(
            Array.isArray(sessionsRes) ? sessionsRes : sessionsRes?.items ?? [],
          );
          const first = list[0] || null;
          yield put({
            type: 'saveCurrentSession',
            payload: isCurrentSession(first) ? first : null,
          });
        } catch {
          yield put({ type: 'saveCurrentSession', payload: null });
        }

        yield put({ type: 'fetchFlows', payload: { id: agentId } });
        try {
          const summary = yield call(getRpaAgentDashboardSummary, agentId);
          yield put({ type: 'saveDashboardSummary', payload: unwrapEntity(summary) });
        } catch {
          yield put({ type: 'saveDashboardSummary', payload: null });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchFlows({ payload }, { call, put }) {
      const agentId = payload?.id;
      if (!agentId) return;
      yield put({ type: 'setFlowsLoading', payload: true });
      try {
        const response = yield call(
          getAll,
          {
            route: `rpa/api/${agentId}/flow/paged`,
            page: 1,
            limit: 100,
            select:
              'id,name,isActive,currentVersionNumber,currentVersionPublished,hasDraftVersion,lastExecutionStatus,lastExecutionStartDateTime',
            orderBy: 'name',
          },
          { suppressErrorNotification: true },
        );
        yield put({ type: 'saveFlows', payload: response?.items || [] });
      } catch {
        yield put({ type: 'saveFlows', payload: [] });
      } finally {
        yield put({ type: 'setFlowsLoading', payload: false });
      }
    },

    *fetchSessions({ payload }, { call, put, select }) {
      const agentId = payload?.id || (yield select((s) => s.rpaAgents?.id));
      if (!agentId) return;
      const reset = payload?.reset !== false;
      const prevSkip = reset ? 0 : yield select((s) => s.rpaAgents?.sessionsSkip || 0);
      yield put({ type: 'setSessionsLoading', payload: true });
      try {
        const res = yield call(fetchRpaAgentSessions, agentId, {
          skip: prevSkip,
          take: SESSIONS_PAGE,
        });
        const page = normalizeSessions(Array.isArray(res) ? res : res?.items ?? []);
        yield put({
          type: 'appendSessions',
          payload: {
            items: page,
            reset,
            skip: prevSkip + page.length,
            hasMore: page.length >= SESSIONS_PAGE,
          },
        });
      } catch {
        if (reset) {
          yield put({
            type: 'appendSessions',
            payload: { items: [], reset: true, skip: 0, hasMore: false },
          });
        }
      } finally {
        yield put({ type: 'setSessionsLoading', payload: false });
      }
    },

    *fetchCreate({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'success', payload: false });
        yield put({ type: 'setLoading', payload: true });
        yield call(createRPAAgent, payload?.data || payload);
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: true },
        });
        yield* refreshRpaLaunchpad(call, put, select);
        yield put({ type: 'success', payload: true });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        yield put({
          type: 'errors',
          payload: {
            validationErrors: e?.validationErrors || [],
            message: e?.message,
          },
        });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchPatchName({ payload }, { call, put, select }) {
      const id = payload?.id;
      const name = payload?.name;
      if (!id || !name) return;
      try {
        yield put({ type: 'success', payload: false });
        yield put({ type: 'setLoading', payload: true });
        yield call(patchRPAAgentName, id, { name });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: true },
        });
        yield* refreshRpaLaunchpad(call, put, select);
        yield put({ type: 'success', payload: true });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        yield put({
          type: 'errors',
          payload: {
            validationErrors: e?.validationErrors || [],
            message: e?.message,
          },
        });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchActivate({ payload }, { call, put, select }) {
      const id = payload?.id;
      const state = payload?.state;
      if (!id) return;
      try {
        yield call(toggleRPAAgentActive, id, state);
        yield put({
          type: 'launchpad/syncItemActive',
          payload: { type: 'rpa', id, isActive: !!state },
        });
        const currentId = yield select((s) => s.rpaAgents?.id);
        if (currentId === id) {
          yield put({ type: 'fetchDetail', payload: { id } });
        }
        yield* refreshRpaLaunchpad(call, put, select);
        yield put({ type: 'success', payload: true });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *fetchRevokeSession({ payload }, { call, put, select }) {
      const id = payload?.id || (yield select((s) => s.rpaAgents?.id));
      if (!id) return;
      yield put({ type: 'setRevokeLoading', payload: true });
      try {
        yield call(revokeRpaAgentSession, id);
        yield put({ type: 'saveCurrentSession', payload: null });
        const showSessions = yield select((s) => s.rpaAgents?.showSessions);
        if (showSessions) {
          yield put({ type: 'fetchSessions', payload: { id, reset: true } });
        }
        yield put({ type: 'fetchDetail', payload: { id } });
        yield put({ type: 'success', payload: true });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setRevokeLoading', payload: false });
      }
    },

    *fetchDelete({ payload }, { call, put, select }) {
      const id = payload?.id || payload;
      if (!id) return;
      try {
        yield call(deleteRPAAgent, id);
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: true },
        });
        yield* refreshRpaLaunchpad(call, put, select);
        yield put({ type: 'success', payload: true });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
      }
    },
  },

  reducers: {
    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: !!payload.state,
        id: payload.state ? payload.id ?? null : null,
        item: payload.state ? payload.item ?? null : null,
        detail: payload.detail !== false,
        success: false,
        validationErrors: [],
        ...(payload.state
          ? {}
          : {
              currentSession: null,
              flows: [],
              dashboardSummary: null,
            }),
      };
    },
    queryShowSessions(state, { payload }) {
      return {
        ...state,
        showSessions: !!payload.state,
        id: payload.state ? payload.id ?? state.id : state.id,
        sessionsAgentName: payload.state ? payload.name ?? null : null,
        ...(payload.state ? { sessions: [], sessionsSkip: 0, sessionsHasMore: false } : {}),
      };
    },
    saveItem(state, { payload }) {
      return { ...state, item: payload };
    },
    saveCurrentSession(state, { payload }) {
      return { ...state, currentSession: payload ? normalizeSession(payload) : null };
    },
    saveFlows(state, { payload }) {
      return { ...state, flows: Array.isArray(payload) ? payload : [] };
    },
    saveDashboardSummary(state, { payload }) {
      return { ...state, dashboardSummary: payload };
    },
    appendSessions(state, { payload }) {
      const items = payload?.items || [];
      return {
        ...state,
        sessions: payload?.reset ? items : [...(state.sessions || []), ...items],
        sessionsSkip: payload?.skip ?? 0,
        sessionsHasMore: !!payload?.hasMore,
      };
    },
    setLoading(state, { payload }) {
      return { ...state, loading: !!payload };
    },
    setFlowsLoading(state, { payload }) {
      return { ...state, flowsLoading: !!payload };
    },
    setSessionsLoading(state, { payload }) {
      return { ...state, sessionsLoading: !!payload };
    },
    setRevokeLoading(state, { payload }) {
      return { ...state, revokeLoading: !!payload };
    },
    success(state, { payload }) {
      return { ...state, success: !!payload };
    },
    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload?.validationErrors || [],
      };
    },
  },
};
